/**
 * @enum {string}
 */
const State = {
  STOPPED: 'stopped',
  RUNNING: 'running',
  PAUSED: 'paused',
}

/**
 * A component used to measure elapsed time.
 *
 * Note: The lapsedTime getter doesn't handle multiple calls very well (it just keeps returning the last value until this.state is changed)
 * Use getLapsedTime() to get an accurate value every time.
 *
 * @param {number=} initialLapsedTime Optional initial lapsed time.
 * @constructor
 *
 * @author Nicoleta Tuns <nicoleta@edulab.dk>
 * Transformed from Google Closure to native ES6 by Thomas Grinderslev <thomas@edulab.dk>
 */
export default class StopWatch {
  /**
   * @global initialTimestamp
   * @type {number}
   * @private
   */
  initialTimestamp

  /**
   * @global state
   * @type {State}
   * @private
   */
  state

  /**
   * @global firstRun
   * @type {boolean}
   * @private
   */
  firstRun

  /**
   * @global lapsedTime
   * @type {number}
   * @private
   */
  lapsedTime_

  constructor(initialLapsedTime) {
    this.initialTimestamp = 0
    this.state = State.STOPPED
    this.firstRun = true
    this.lapsedTime_ = initialLapsedTime || 0
  }

  /**
   * Counts the time passed from the moment the function is called.
   * @public
   */
  start() {
    if (!this.firstRun && this.state === State.STOPPED) {
      this.lapsedTime_ = 0
    }

    if (this.state !== State.RUNNING) {
      this.initialTimestamp = Date.now()
      this.state = State.RUNNING
    }

    this.firstRun = false
  }

  /**
   * Pauses the stopwatch and adds elapsed time to the total amount of time counted
   * @public
   */
  pause() {
    if (this.initialTimestamp === 0) {
      return
    }

    this.state = State.PAUSED

    this.lapsedTime_ += this.calculateLapsedTime()
  }

  /**
   * Toggles between the pause and running state
   * @returns {boolean} true if the stop watch is now running
   * @public
   */
  togglePause() {
    if (this.state === State.RUNNING) {
      this.pause()
      return false
    } else {
      this.start()
      return true
    }
  }

  /**
   * Stops the stopwatch and resets the initial timestamp to be used for a new counting session.
   * @public
   */
  stop() {
    this.lapsedTime_ = this.getLapsedTime()

    this.initialTimestamp = 0

    this.state = State.STOPPED
  }

  /**
   * Gets the elapsed time since the stopwatch was started.
   * @returns {number}
   * @public
   */
  getLapsedTime() {
    if (this.state === State.RUNNING) {
      return this.lapsedTime_ + this.calculateLapsedTime()
    }

    return this.lapsedTime_
  }

  /**
   * Gets the elapsed time since the stopwatch was started.
   * @deprecated
   * @returns {number}
   * @public
   */
  get lapsedTime() {
    return this.getLapsedTime()
  }

  /**
   * Return true if the stop watch is running
   * @returns {boolean}
   * @public
   */
  get isRunning() {
    return this.state === State.RUNNING
  }

  /**
   * Return true if the stop watch is paused
   * @returns {boolean}
   * @public
   */
  get isPaused() {
    return this.state === State.PAUSED
  }

  /**
   * Return true if the stop watch is running
   * @returns {boolean}
   * @public
   */
  get isStopped() {
    return this.state === State.STOPPED
  }

  /**
   * Calculates the time measured since last start.
   * @returns {number}
   * @private
   */
  calculateLapsedTime() {
    return Date.now() - this.initialTimestamp
  }
}
